// src/components/Sidebar.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SIDEBAR_WIDTH } from '../theme/theme';
import { ChildAvatar } from './SharedComponents';
import { getRouteByBackendRoute, YEARBOOK_HOME_ROUTE } from '../yearbookRoutes';

export default function Sidebar({
  bookConfig,
  children: childrenData,
  activeChildId,
  onSwitchChild,
  activeNav,
  onNavigate,
  mobileOpen,
  onMobileClose,
  onLogout,
  sections = [] // <--- New dynamic sections array from /dashboard API
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [childMenuAnchor, setChildMenuAnchor] = useState(null);

  const activeChild = useMemo(
    () =>
      childrenData?.find((child) => child.id === activeChildId) ||
      childrenData?.[0] ||
      null,
    [childrenData, activeChildId]
  );

  const childMenuOpen = Boolean(childMenuAnchor);

  const handleChildMenuOpen = (event) => {
    setChildMenuAnchor(event.currentTarget);
  };

  const handleChildMenuClose = () => {
    setChildMenuAnchor(null);
  };

  const handleChildSwitch = (childId) => {
    setChildMenuAnchor(null);
    onSwitchChild(childId);

    if (isMobile) {
      onMobileClose?.();
    }
  };

  // Convert flat API sections[] -> grouped UI structure using the shared route config.
  // let visibleSections = sections//.filter((section) => section.visible);
  let visibleSections = sections.filter((section) => section.key !== 'cartoon' && section.key !== 'shipping')
  console.log('visibleSections', visibleSections);
  // if (!visibleSections.some((s) => s.key === 'cartoon')) {
  //   visibleSections.push({
  //     key: 'cartoon',
  //     route: 'cartoon',
  //     visible: false
  //   });
  // }

  // const visibleSections =
  //   [{
  //     key: 'cover',
  //     route: 'cover',
  //     visible: true
  //   },
  //   {
  //     key: 'cartoon',
  //     route: 'cartoon',
  //     visible: true
  //   }].filter((section) => section.visible);
  const dynamicNav = [];

  visibleSections.forEach((section) => {
    const route = getRouteByBackendRoute(section.route);
    if (!route || !route.group) return;

    let groupObj = dynamicNav.find((group) => group.group === route.group);
    if (!groupObj) {
      groupObj = { group: route.group, items: [] };
      dynamicNav.push(groupObj);
    }

    groupObj.items.push({
      key: route.key,
      label: route.key === 'order'
        ? 'Order & Delivery'
        : route.key === 'letter'
          ? route.label
          : (section.label || route.label),
      icon: route.icon,
      progress: section.progress,
    });
  });

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        {/* <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px' }}>
          MakeMyVity
        </Typography> */}
        <img src='https://makemyvity.com/myVityLogoBlack.webp' alt="Company Logo" width="120" />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 2 } }}>

        {/* Child Switcher */}
        {activeChild && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                pl: 0.5,
                mb: 1,
                display: 'block',
              }}
            >
              Child
            </Typography>

            <ListItemButton
              onClick={handleChildMenuOpen}
              sx={{
                borderRadius: 2.5,
                py: 1,
                px: 1.5,
                bgcolor: '#fff',
                boxShadow: 1,
                '&:hover': {
                  bgcolor: '#fff',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ChildAvatar
                  url={activeChild.profile_url}
                  first_name={activeChild.first_name}
                  last_name={activeChild.last_name}
                  gender={activeChild.gender}
                />
              </ListItemIcon>

              <ListItemText
                primary={`${activeChild.first_name || ''} ${activeChild.last_name || ''}`.trim()}
                secondary={activeChild.classroom}
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 600,
                  noWrap: true,
                }}
                secondaryTypographyProps={{
                  fontSize: 11,
                  color: 'text.disabled',
                  noWrap: true,
                }}
              />

              {childrenData?.length > 1 && (
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: 20,
                    color: 'text.secondary',
                    transform: childMenuOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                    transition: 'transform 150ms ease',
                  }}
                />
              )}
            </ListItemButton>

            {childrenData?.length > 1 && (
              <Menu
                anchorEl={childMenuAnchor}
                open={childMenuOpen}
                onClose={handleChildMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.75,
                      width: 250,
                      maxHeight: 360,
                      borderRadius: 2,
                    },
                  },
                }}
              >
                {childrenData.map((child) => {
                  const selected = child.id === activeChildId;

                  return (
                    <MenuItem
                      key={child.id}
                      selected={selected}
                      onClick={() => handleChildSwitch(child.id)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        gap: 1.25,
                      }}
                    >
                      <ChildAvatar
                        url={child.profile_url}
                        first_name={child.first_name}
                        last_name={child.last_name}
                        gender={child.gender}
                      />

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: selected ? 700 : 500,
                            lineHeight: 1.25,
                          }}
                          noWrap
                        >
                          {`${child.first_name || ''} ${child.last_name || ''}`.trim()}
                        </Typography>

                        {child.classroom && (
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: 'text.secondary',
                            }}
                            noWrap
                          >
                            {child.classroom}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
          </Box>
        )}

        {/* Overview Link */}
        <Box sx={{ px: 2, pb: 1 }}>
          <ListItemButton
            selected={activeNav === YEARBOOK_HOME_ROUTE.key}
            onClick={() => onNavigate(YEARBOOK_HOME_ROUTE.key)}
            sx={{
              borderRadius: 2,
              py: 0.9,
              px: 1.5,
              bgcolor: activeNav === YEARBOOK_HOME_ROUTE.key ? '#fff' : 'transparent',
              boxShadow: activeNav === YEARBOOK_HOME_ROUTE.key ? 1 : 0,
              '&.Mui-selected': { bgcolor: '#fff' },
              '&.Mui-selected:hover': { bgcolor: '#fff' },
              '&:hover': { bgcolor: activeNav === YEARBOOK_HOME_ROUTE.key ? '#fff' : 'grey.200' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Typography sx={{ fontSize: 15, width: 22, textAlign: 'center' }}>{YEARBOOK_HOME_ROUTE.icon}</Typography>
            </ListItemIcon>
            <ListItemText
              primary={YEARBOOK_HOME_ROUTE.label}
              primaryTypographyProps={{
                fontSize: 13.5,
                fontWeight: activeNav === YEARBOOK_HOME_ROUTE.key ? 600 : 500,
                color: activeNav === YEARBOOK_HOME_ROUTE.key ? 'text.primary' : 'text.secondary',
              }}
            />
          </ListItemButton>
        </Box>

        {/* Dynamic Nav Groups */}
        <Box sx={{ px: 2, pb: 2 }}>
          {dynamicNav.map((group) => (
            <Box key={group.group} sx={{ mb: 2.5 }}>
              <Typography variant="caption" sx={{ pl: 0.5, mb: 0.75, display: 'block' }}>
                {group.group}
              </Typography>
              {group.items.map((item) => {
                const active = activeNav === item.key;
                return (
                  <ListItemButton
                    key={item.key}
                    selected={active}
                    onClick={() => onNavigate(item.key)}
                    sx={{
                      borderRadius: 2,
                      py: 0.9,
                      px: 1.5,
                      mb: 0.25,
                      bgcolor: active ? '#fff' : 'transparent',
                      boxShadow: active ? 1 : 0,
                      '&.Mui-selected': { bgcolor: '#fff' },
                      '&.Mui-selected:hover': { bgcolor: '#fff' },
                      '&:hover': { bgcolor: active ? '#fff' : 'grey.200' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>
                      <Typography sx={{ fontSize: 15, width: 22, textAlign: 'center' }}>{item.icon}</Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: active ? 600 : 500,
                        color: active ? 'text.primary' : 'grey.700',
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer Area: Logout */}
      <Box sx={{ px: 2, pb: 2.5, pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <ListItemButton
          onClick={onLogout}
          sx={{
            borderRadius: 2,
            py: 0.9,
            px: 1.5,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'grey.200', color: 'error.main' },
            transition: 'color 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
            <LogoutIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Log out"
            primaryTypographyProps={{
              fontSize: 13.5,
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100vh',
        overflowY: 'auto',
        flexShrink: 0,
        '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, position: 'relative' },
      }}
    >
      {content}
    </Drawer>
  );
}